import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, skipWhile, tap } from 'rxjs';
import { LoggerService } from '../../v2/core/services/logger.service';
import { GSEvent, GSOrgContext, GSUserContext } from './gainsight.types';

export enum AptrinsicAction {
  Identify = 'identify',
  Reset = 'reset',
  Set = 'set',
  Track = 'track',
}
const APTRINSIC = 'aptrinsic';

@Injectable({
  providedIn: 'root',
})
export class GainsightService {
  private readonly logger = inject(LoggerService);

  aptrinsic: any;
  private readonly waitUntilInitializedOrErrorSub$ = new BehaviorSubject<boolean>(false);

  waitUntilInitializedOrError$(): Observable<boolean> {
    let attemptCount = 0;
    const maxTries = 20;

    const checkInitInterval = setInterval(() => {
      this.checkIfInitialized();
      attemptCount = attemptCount + 1;
      if (attemptCount >= maxTries) {
        clearInterval(checkInitInterval);
        this.logger.error('Gainsight initialization failed after maximum attempts');
        this.waitUntilInitializedOrErrorSub$.next(true);
      }
    }, 50);
    return this.waitUntilInitializedOrErrorSub$.asObservable().pipe(
      skipWhile((initialized) => initialized === false),
      tap((_) => {
        clearInterval(checkInitInterval);
      }),
    );
  }

  private checkIfInitialized(): void {
    const aptrinsic = (globalThis as any)[APTRINSIC];
    this.logger.log('aptrinsic', aptrinsic);
    if (aptrinsic?.init) {
      this.logger.log('Gainsight initialized');
      this.waitUntilInitializedOrErrorSub$.next(true);
      this.aptrinsic = aptrinsic;
    }
  }

  private doAptrinsic(action): void {
    if (!this.aptrinsic) {
      return;
    }
    try {
      action(this.aptrinsic);
    } catch (error) {
      this.logger.error(error);
    }
  }
  pushUserProfile(userIdentity: GSUserContext): void {
    this.doAptrinsic((aptrinsic) => {
      const account = {
        id: userIdentity.domain,
        emailDomain: userIdentity.domain,
        raBusinessPartnerID: userIdentity.raBusinessPartnerID,
        name: userIdentity.company,
      };
      aptrinsic(AptrinsicAction.Identify, userIdentity, account);
    });
  }

  reset(): void {
    this.doAptrinsic((aptrinsic) => {
      aptrinsic(AptrinsicAction.Reset);
    });
  }

  setGlobalContext(context: GSOrgContext): void {
    this.doAptrinsic((aptrinsic) => {
      aptrinsic(AptrinsicAction.Set, 'globalContext', context);
    });
  }

  pushCustomEvent({ eventName, eventPayload }: GSEvent): void {
    this.doAptrinsic((aptrinsic) => {
      aptrinsic(AptrinsicAction.Track, eventName, eventPayload);
    });
  }
}
